
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Dropdown } from '@shared/ui-react/Dropdown'
import { MapToolsStore } from '@openlayers/MapToolsStore';
import { FileTreeService } from '@shared/services/FileTreeService';
import { Tree as TreeReact } from '@shared/ui-react/Tree';
import { TreeNode } from '@shared/ui-native/Tree';
import { countsIncludesModels } from '@shared/utils/utils';
import { Toggle } from '@shared/ui-react/Toggle';
import { CalcSurface, CalcSurfaceSchema } from '../../../../../../typings/reality-capture/volumes/models';
import { treeNodeToCalcSurface, fetchModelOutlineArea } from '../../utils/helpers/helpers';
import { Button } from '@shared/ui-react/Button';

export type SurfaceOption = 'dem' | 'model' | ''

export const SelectSurface = ({
    toolsStore,
    selection,
    onSelect,
    onClear,
    disableOptions = false,
}: {
    toolsStore: MapToolsStore,
    selection: CalcSurface,
    onSelect: (value: CalcSurface) => void,
    onClear: () => void,
    disableOptions?: boolean,
}) => {
    const tileLayers = useMemo(() => toolsStore.mapWrapper.tileLayers, [toolsStore]);
    const [selectedOption, setSelectedOption] = useState<SurfaceOption>(disableOptions ? 'dem' : '');
    const [fileTreeData, setFileTreeData] = useState<ITreeData>();

    useEffect(() => {
        if (selection?.surfaceType) {
            setSelectedOption(selection.surfaceType)
        } else {
            setSelectedOption(disableOptions ? 'dem' : '')
        }
    }, [selection?.surfaceType, disableOptions])

    useEffect(() => {
        async function getFileTree() {
            const fileTree = await FileTreeService.fetchFileTree(true)
            setFileTreeData(fileTree);
        }
        getFileTree();
    }, []);

    return (
        <div className="flex items-stretch gap-4">
            <div className="flex flex-col gap-2 w-fit min-w-0">
                {!disableOptions && (
                    <div className="flex bg-ik-surface-dim rounded-md p-1 border border-ik-border-weak w-fit">
                        <div 
                            className={`px-4 py-1 rounded text-xs font-medium transition-all cursor-pointer select-none ${selectedOption === 'dem' ? 'bg-ik-accent-primary text-white shadow-sm' : 'text-ik-text-default hover:bg-ik-surface-hover'}`}
                            onClick={() => {
                                if (selectedOption !== 'dem') {
                                    onClear();
                                    setSelectedOption('dem');
                                }
                            }}
                        >
                            {__('js_map_measuring_tools.dem')}
                        </div>
                        <div 
                            className={`px-4 py-1 rounded text-xs font-medium transition-all cursor-pointer select-none ${selectedOption === 'model' ? 'bg-ik-accent-primary text-white shadow-sm' : 'text-ik-text-default hover:bg-ik-surface-hover'}`}
                            onClick={() => {
                                if (selectedOption !== 'model') {
                                    onClear();
                                    setSelectedOption('model');
                                }
                            }}
                        >
                            {__('js_map_measuring_tools.model')}
                        </div>
                    </div>
                )}

                <div className="flex-1 w-full">
                    {selectedOption === 'dem' && (
                        <SelectDem
                            fileTreeData={fileTreeData}
                            tileLayers={tileLayers}
                            selection={selection}
                            onSelect={onSelect}
                        />
                    )}
                    {selectedOption === 'model' && (
                        <SelectModel
                            fileTreeData={fileTreeData}
                            toolsStore={toolsStore}
                            selection={selection}
                            onSelect={onSelect}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const SelectDem = ({ fileTreeData, tileLayers, selection, onSelect }) => {
        const [isOpen, setIsOpen] = useState(false)
        const [showAll, setShowAll] = useState(false)
        const [selectedDems, setSelectedDems] = useState<CalcSurface[]>([])
        const ignoreNextCloseRef = useRef(false)

        const demLookup = useMemo(() => {
            const uuidToLayerDef = new Map<string, any>()
            const addKey = (key: unknown, layerDef: any) => {
                if (typeof key !== 'string' || !key) return
                if (!uuidToLayerDef.has(key)) {
                    uuidToLayerDef.set(key, layerDef)
                }
            }
            const demLayerDefs = (tileLayers.layersDefinitions ?? []).filter(l => !!l?.demUrl)
            demLayerDefs.forEach(layerDef => {
                addKey(layerDef?.fileUuid, layerDef)
                addKey(layerDef?.uuid, layerDef)
            })
            return { uuidToLayerDef }
        }, [tileLayers.layersDefinitions])

        const childFilter = useCallback((c: any) => {
        if (c?.tolerance) return true
        const candidates = [c?.uuid, c?.headUuid, c?.modelUuid, c?.model?.uuid].filter(Boolean)
        return candidates.some(uuid => demLookup.uuidToLayerDef.has(uuid))
    }, [demLookup.uuidToLayerDef])

    const selectedUuid = useMemo(() => {
        if (!selection || typeof selection?.uuid !== 'string') return null
        const uuid = selection.uuid
        const match = demLookup.uuidToLayerDef.get(uuid)
        if (match?.fileUuid) return match.fileUuid
        return uuid
    }, [selection?.uuid, demLookup.uuidToLayerDef])

    useEffect(() => {
        const activeUuids = tileLayers.activeUuids;
        const selectedDemLayers: CalcSurface[] = (tileLayers.layersDefinitions ?? [])
            .filter(l => !!l?.demUrl && activeUuids.includes(l.uuid))
            .map(l => ({
                uuid: l.uuid,
                demUrl: l.demUrl,
                text: l.name,
            }))
        setSelectedDems(selectedDemLayers)
    }, [tileLayers.activeUuids, tileLayers.layersDefinitions])

    const selectAndClose = useCallback((calcSurface: CalcSurface) => {
        const selectedSurface = CalcSurfaceSchema.parse(calcSurface)
        onSelect(selectedSurface)
        setIsOpen(false)
    }, [onSelect])

    const selectFromFileTree = useCallback((node: any): void => {
        const uuid = node.uuid
        const layer = demLookup.uuidToLayerDef.get(uuid);
        const calcSurface: CalcSurface = {
            surfaceType: 'dem',
            surfaceDemUrl: layer.demUrl,
            text: node.text,
        }
        selectAndClose(calcSurface)
    }, [demLookup.uuidToLayerDef, selectAndClose])

    const selectFromDropdown = useCallback((layer) => {
        const calcSurface: CalcSurface = {
            surfaceType: 'dem',
            surfaceDemUrl: layer.demUrl,
            text: layer.text,
        }
        selectAndClose(calcSurface)
    }, [selectAndClose])

    const dropdown = useMemo(() => (
        <Dropdown
            className="!border !border-ik-border-strong !border-solid !bg-ik-surface-default min-w-[140px] rounded-md text-ik-text-default px-3 py-1 transition-colors hover:!bg-ik-surface-dim text-sm"
            closeOnBodyClick={false}
            styledBody={false}
            isOpen={isOpen}
            onToggle={(nextOpen) => {
                if (!nextOpen && ignoreNextCloseRef.current) return
                setIsOpen(nextOpen)
            }}
            bodyStyle={{ maxWidth: 'min(700px, 100vw)' }}
        >
            <span className="flex items-center justify-between cursor-pointer w-full text-sm overflow-hidden">
                <span className="truncate min-w-0">
                    {selection
                        ? selection.text
                        : __('js_map_measuring_tools.select')}
                </span>
                <i className="iconf-caret ml-2 shrink-0" />
            </span>
            <div className="bg-ik-surface-default !border !border-ik-border-default rounded-md shadow-lg mt-1 p-2 text-sm">
                <div className="toggle-orange">
                    <Toggle
                        checked={showAll}
                        onClick={() => {
                            ignoreNextCloseRef.current = true
                            setShowAll(v => !v)
                        }}
                    />
                </div>
                {showAll ? (
                    fileTreeData ? (
                        <div style={{ borderTop: '1px solid var(--ik-color-border-default)', background: 'var(--ik-color-surface-default)', maxHeight: 300, overflow: 'auto' }}>
                            <TreeReact
                                treeData={fileTreeData}
                                showRoot={false}
                                includesFiles={true}
                                headerControls={true}
                                hideEmptyFoldersAlways={true}
                                hideEmptyFolders={true}
                                childFilter={childFilter}
                                selectedUuid={selectedUuid}
                                selectFallbackToRoot={false}
                                onSelect={node => {
                                    if (node?.isFile?.()) {
                                        selectFromFileTree(node)
                                    }
                                }}
                                expandOnStart="firstLevel"
                                showContextMenu={false}
                            />
                        </div>
                    ) : null
                ) :
                (<DropdownList layers={selectedDems} onSelect={selectFromDropdown} />)}
            </div>
        </Dropdown>
    ), [isOpen, showAll, fileTreeData, selectedDems, selection?.text, childFilter, selectFromFileTree, selectFromDropdown, selectedUuid])

    return (
        <div className="flex items-center gap-2">
            {dropdown}
        </div>
        )
    }

export const SelectModel = ({ fileTreeData, toolsStore, selection, onSelect }) => {
    const [models, setModels] = useState<TreeNode[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const ignoreNextCloseRef = useRef(false);

    const selectedUuid = selection && typeof selection?.uuid === 'string' ? selection.uuid : null
    const childFilter = useCallback((c: any) => {
        if (c?.tolerance) return true
        if (c?.model?.layerCounts && countsIncludesModels(c.model.layerCounts)) return true
        if (c?.layerCounts && countsIncludesModels(c.layerCounts)) return true
        return false
    }, [])

    useEffect(() => {
        const modelData = toolsStore.getSelectedModelNodes ? toolsStore.getSelectedModelNodes() : [];
        setModels(modelData)
    }, [])

    const selectAndClose = useCallback((node: TreeNode) => {
        const calcSurface: CalcSurface = treeNodeToCalcSurface(node);
        onSelect(calcSurface)
        setIsOpen(false)
    }, [onSelect])

    const dropdown = useMemo(() => (
        <Dropdown
            className="!border !border-ik-border-strong !border-solid !bg-ik-surface-default min-w-[140px] rounded-md text-ik-text-default px-3 py-1 transition-colors hover:!bg-ik-surface-dim text-sm"
            closeOnBodyClick={false}
            styledBody={false}
            isOpen={isOpen}
            onToggle={(nextOpen) => {
                if (!nextOpen && ignoreNextCloseRef.current) return
                setIsOpen(nextOpen)
            }}
            bodyStyle={{ maxWidth: 'min(700px, 100vw)' }}
        >
            <span className="flex items-center justify-between cursor-pointer w-full text-sm overflow-hidden">
                <span className="truncate min-w-0">
                    {selection
                        ? selection.text
                        : __('js_map_measuring_tools.select')}
                </span>
                <i className="iconf-caret ml-2 shrink-0" />
            </span>
            <div className="bg-ik-surface-default !border !border-ik-border-default rounded-md shadow-lg mt-1 p-2 text-sm">
                <Toggle
                    checked={showAll}
                    onClick={() => {
                        ignoreNextCloseRef.current = true
                        setShowAll(v => !v)
                    }}
                />
                {showAll ? (
                    fileTreeData ? (
                        <div style={{ borderTop: '1px solid var(--ik-color-border-default)', background: 'var(--ik-color-surface-default)', maxHeight: 300, overflow: 'auto' }}>
                            <TreeReact
                                treeData={fileTreeData}
                                showRoot={false}
                                includesFiles={true}
                                headerControls={true}
                                hideEmptyFoldersAlways={true}
                                hideEmptyFolders={true}
                                childFilter={childFilter}
                                selectedUuid={selectedUuid}
                                selectFallbackToRoot={false}
                                onSelect={node => {
                                    if (node?.isFile?.()) {
                                        selectAndClose(node)
                                    }
                                }}
                                expandOnStart="firstLevel"
                                showContextMenu={false}
                            />
                        </div>
                    ) : null
                ) : 
                <DropdownList layers={models} onSelect={selectAndClose} />}
            </div>
        </Dropdown>
    ), [isOpen, showAll, fileTreeData, models, selection?.text, childFilter, selectAndClose, selectedUuid])

    return (
            <div className="flex items-center gap-2">
                {dropdown}
            </div>
        )
}

export const DropdownList = ({ layers, onSelect }) => (
        <ul className="dropdown-list clickable">
            {layers?.length ? (
                layers.map((layer) => (
                    <li key={layer.uuid ?? layer.id} onClick={() => onSelect(layer)} className="px-2 py-1 hover:bg-ik-surface-dim cursor-pointer">
                        {layer.text}
                    </li>
                ))
            ) : (
                <li className="px-2 py-1 text-ik-text-weak">{__('js_map_measuring_tools.no_layers_available')}</li>
            )}
        </ul>
    )

export const ButtonSelectSurface = ({
    toolsStore,
    selection,
    onSelect,
    onClear,
}: {
    toolsStore: MapToolsStore;
    selection: CalcSurface;
    onSelect: (value: CalcSurface) => void;
    onClear: () => void;
}) => {
    const tileLayers = useMemo(() => toolsStore.mapWrapper.tileLayers, [toolsStore]);
    const [selectedOption, setSelectedOption] = useState<SurfaceOption>(selection?.surfaceType || '');
    const [fileTreeData, setFileTreeData] = useState<ITreeData>();

    useEffect(() => {
        setSelectedOption(selection?.surfaceType || '');
    }, [selection?.surfaceType]);

    useEffect(() => {
        async function getFileTree() {
            const fileTree = await FileTreeService.fetchFileTree(true);
            setFileTreeData(fileTree);
        }
        getFileTree();
    }, []);

    const handleOptionClick = (option: SurfaceOption) => {
        if (selectedOption === option) return;
        onClear();
        setSelectedOption(option);
    };

    return (
        <div className="flex items-center gap-2 flex-wrap">
            <div className="flex bg-ik-surface-dim rounded-md p-1 border border-ik-border-weak w-fit">
                <div
                    className={`px-4 py-1 rounded text-xs font-medium transition-all cursor-pointer select-none ${selectedOption === 'dem' ? 'bg-ik-accent-primary text-white shadow-sm' : 'text-ik-text-default hover:bg-ik-surface-hover'}`}
                    onClick={() => handleOptionClick('dem')}
                >
                    {__('js_map_measuring_tools.dem')}
                </div>
                <div
                    className={`px-4 py-1 rounded text-xs font-medium transition-all cursor-pointer select-none ${selectedOption === 'model' ? 'bg-ik-accent-primary text-white shadow-sm' : 'text-ik-text-default hover:bg-ik-surface-hover'}`}
                    onClick={() => handleOptionClick('model')}
                >
                    {__('js_map_measuring_tools.model')}
                </div>
            </div>
            {selectedOption === 'dem' && (
                <SelectDem
                    fileTreeData={fileTreeData}
                    tileLayers={tileLayers}
                    selection={selection}
                    onSelect={onSelect}
                />
            )}
            {selectedOption === 'model' && (
                <div className="flex items-center gap-2">
                    <SelectModel
                        fileTreeData={fileTreeData}
                        toolsStore={toolsStore}
                        selection={selection}
                        onSelect={onSelect}
                    />
                    {selection?.surfaceModelUuid && (
                        <Button
                            secondary
                            small
                            onClick={() => { fetchModelOutlineArea(toolsStore, selection.surfaceModelUuid); }}
                        >
                            {__('js_map_measuring_tools.use_model_outline')}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};